import React, { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { activeIndicatorsModalRef } from "../Modals/ActiveIndicatorsModalRef";
import { Button } from "primereact/button";
// SplitButton removed — replaced by individual dock panel buttons
import { Divider } from "primereact/divider";
import { Toast } from "primereact/toast";
// import { useNavigate } from 'react-router-dom'; // (not used)
import { useChartsStore } from "~/store/chartStore";
import { useSidebarFavoritesStore } from "~/store/sidebarFavoritesStore";
import useWorkspaceStore from "~/store/workspaceStore";
import useWatchlistStore from "~/store/watchlistStore";
import useBookByPriceStore from "~/store/bookByPriceStore";
import { useAuthStore } from "~/store/authStore";
import "./Navbar.css";
import { PANEL_CAPABILITIES } from "./SymbolSpotlight";
import SymbolDropdown from "./SymbolDropdown";
import ChartTypeDropdown from "./ChartTypeDropdown";
import BarColorDropdown from "./BarColorDropdown";
import BarColorModal from "./BarColorModal";
import { isFeatureEnabled } from "../../data/Services/settings";
import { useNavbarChartControls } from "../../store/deviceSettingsStore";
import ConfigPanel from "./ConfigPanel";
// RouterLoginButton import moved to RouterOperationsSidebar
import PeriodicityDropdown from "./PeriodicityDropdown";
import RegistryIndicatorsDropdown from "./RegistryIndicatorsDropdown";
import PropTypes from "prop-types";
import { isMobilePhone } from "../../utils/mobileDetection";
import { isWebOnWindows } from "../../desktop/platform.js";
import useNarrowViewport from "../../hooks/useNarrowViewport";

// Detect iPad (including iPadOS 13+ which reports as MacIntel)
const isIPad =
  /iPad/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

// Feature flag: Debug panels - set VITE_DEBUG_PANELS=false to exclude from bundle
const DEBUG_PANELS_ENABLED = import.meta.env.VITE_DEBUG_PANELS === "true";

// Lazy load Debug components only when enabled
const IndicatorBatchTester = DEBUG_PANELS_ENABLED
  ? lazy(() => import("../Debug/IndicatorBatchTester"))
  : () => null;

// Lazy load heavy modals
const ActiveIndicatorsModal = lazy(
  () => import("../Modals/ActiveIndicatorsModal"),
);
const ChartSelectionModal = lazy(() =>
  import("../ChartSelectionModal/ChartSelectionModalEnhanced").then((m) => ({
    default: m.ChartSelectionModal,
  })),
);
// SVG components for pencil icons that respect currentColor
const EditPenOn = ({ className, width = 20, height = 20 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={width}
    height={height}
    viewBox="0 0 24 24"
    className={className}
    aria-label="Pencil (filled)"
  >
    <path
      d="M3 17V21H7L18 10 14 6 3 17ZM21 7 17 3 15 5 19 9 21 7Z"
      fill="currentColor"
    />
  </svg>
);

const EditPenOff = ({ className, width = 20, height = 20 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={width}
    height={height}
    viewBox="0 0 24 24"
    className={className}
    aria-label="Pencil (outline)"
  >
    <path
      d="M3 17V21H7L18 10 14 6 3 17ZM21 7 17 3 15 5 19 9 21 7Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="miter"
      strokeLinecap="butt"
    />
  </svg>
);

EditPenOn.propTypes = {
  className: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
};

EditPenOff.propTypes = {
  className: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
};

function Navbar() {
  const routerOperationsSidebarOpen = useWorkspaceStore(
    (state) => state.isRouterOperationsSidebarOpen,
  );
  const toggleRouterOperationsSidebar = useWorkspaceStore(
    (state) => state.toggleRouterOperationsSidebar,
  );
  const toggleSidebar = useSidebarFavoritesStore(
    (state) => state.toggleSidebar,
  );
  const sidebarOpen = useSidebarFavoritesStore((state) => state.sidebarOpen);

  // Get login method to conditionally show chart selector
  const isAuthenticatedViaBrokerAuth = useAuthStore(
    (state) => state.isAuthenticatedViaBrokerAuth,
  );

  const dockPanels = useWorkspaceStore((state) => state.dockPanels);
  const addDockPanel = useWorkspaceStore((state) => state.addDockPanel);

  const addNewChart = useChartsStore((state) => state.addNewChart);

  // Focused panel capabilities — drives navbar control visibility
  const focusedPanelType = useWorkspaceStore((state) => state.focusedPanelType);
  const capabilities =
    PANEL_CAPABILITIES[focusedPanelType] || PANEL_CAPABILITIES.chart;
  const canSymbol = capabilities.includes("symbol");
  const canPeriodicity = capabilities.includes("periodicity");
  const canChartType = capabilities.includes("charttype");
  const canIndicator = capabilities.includes("indicator");
  const canBarColor = capabilities.includes("barcolor");
  const disabledStyle = { opacity: 0.35, pointerEvents: "none" };
  // Controles de config do gráfico na navbar (Período, Estudos, Estudos Ativos,
  // Tipo, Cores). Escondidos por escolha do usuário no device-setting
  // navbarChartControls (default: visíveis); a alternativa de acesso é o menu de
  // contexto do gráfico, sempre ligado. Símbolo permanece visível (display do
  // símbolo atual + único acesso p/ watchlist/book). CSS hide, NÃO unmount, onde
  // o controle hospeda um modal/ref que o menu depende: RegistryIndicatorsDropdown
  // (picker de Estudos + indicatorsPickerRef) e ActiveIndicatorsModal
  // (activeIndicatorsModalRef). Hook reativo → togglar em Configurações re-renderiza.
  const hideChartControls = !useNavbarChartControls();
  const phoneHiddenStyle = { display: "none" };

  // Detect platform for keyboard shortcuts display
  const modKey = navigator.platform.includes("Mac") ? "⌘" : "Ctrl";
  // const navigate = useNavigate(); // (not used)

  const toastRef = React.useRef(null);

  // Narrow viewport detection for collapsible right toolbar
  const isNarrowViewport = useNarrowViewport(768);
  const [rightToolbarOpen, setRightToolbarOpen] = useState(false);
  const toolbarPopupRef = React.useRef(null);
  const toolbarTriggerRef = React.useRef(null);

  // Batch tester state (only in development mode)
  const [batchTesterVisible, setBatchTesterVisible] = useState(false);

  // Chart selector modal state
  const [chartSelectorVisible, setChartSelectorVisible] = useState(false);
  const [authToken, setAuthToken] = useState(null);

  // Fullscreen state - tracks browser fullscreen
  // document.fullscreenEnabled is false on iPhone Safari (API unsupported), true on iPad/desktop/Android
  const fullscreenSupported = !!document.fullscreenEnabled;
  const [isFullscreen, setIsFullscreen] = useState(
    !!document.fullscreenElement,
  );

  useEffect(() => {
    const onFullscreenChange = () =>
      setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  // Close right toolbar popup when viewport goes wide
  useEffect(() => {
    if (!isNarrowViewport) setRightToolbarOpen(false);
  }, [isNarrowViewport]);

  // Click-outside to close right toolbar popup
  useEffect(() => {
    if (!rightToolbarOpen) return;
    const handleMouseDown = (e) => {
      // Ignore clicks inside PrimeReact portals (dialogs, dropdowns, overlays)
      // These render on document.body outside the popup container
      if (
        e.target.closest(
          ".p-dialog-mask, .p-dropdown-panel, .p-overlaypanel, .p-tooltip",
        )
      )
        return;
      if (
        toolbarPopupRef.current &&
        !toolbarPopupRef.current.contains(e.target) &&
        toolbarTriggerRef.current &&
        !toolbarTriggerRef.current.contains(e.target)
      ) {
        setRightToolbarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [rightToolbarOpen]);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen();
    }
  }, []);

  // Chart selector highlight state (badge + pulse animation)
  const [showChartSelectorHighlight, setShowChartSelectorHighlight] = useState(
    !localStorage.getItem("chart-selector-tip-shown-v1"),
  );
  const chartSelectorButtonRef = React.useRef(null);

  // Listen for debug indicator batch tester event
  useEffect(() => {
    const handleOpenBatchTester = () => {
      setBatchTesterVisible(true);
    };

    window.addEventListener("openIndicatorBatchTester", handleOpenBatchTester);
    return () =>
      window.removeEventListener(
        "openIndicatorBatchTester",
        handleOpenBatchTester,
      );
  }, []);

  // Load auth token for chart selector modal
  useEffect(() => {
    const token = sessionStorage.getItem("original-auth-token");
    if (token) {
      setAuthToken(token);
    }
  }, []);

  // Chart selector highlight logic: show tip 2 seconds after component mounts
  useEffect(() => {
    if (showChartSelectorHighlight && chartSelectorButtonRef.current) {
      const timer = setTimeout(() => {
        // Trigger tooltip by focusing the button
        chartSelectorButtonRef.current?.focus();

        // Hide tooltip after 6 seconds
        const hideTimer = setTimeout(() => {
          chartSelectorButtonRef.current?.blur();
        }, 6000);

        // Remove pulse animation after 30 seconds
        const removeHighlightTimer = setTimeout(() => {
          setShowChartSelectorHighlight(false);
          localStorage.setItem("chart-selector-tip-shown-v1", "true");
        }, 30000);

        return () => {
          clearTimeout(hideTimer);
          clearTimeout(removeHighlightTimer);
        };
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [showChartSelectorHighlight]);

  // handleLogout removed (not used)

  const createNewTab = async () => {
    await addNewChart();
  };

  // dockPanelMenuItems removed — replaced by individual navbar buttons

  const handleOpenChartSelector = () => {
    // Remove highlight and save preference when user clicks
    if (showChartSelectorHighlight) {
      setShowChartSelectorHighlight(false);
      localStorage.setItem("chart-selector-tip-shown-v1", "true");
    }
    setChartSelectorVisible(true);
  };

  const handleNewWorkspace = () => {
    // Clear current workspace and add a new chart
    clearWorkspace();
    // Reset dock layout so panels (Positions, Open Orders, Log) are closed (#200)
    useWorkspaceStore.getState().setDockLayout(null);
    useWorkspaceStore.getState().setRouterOperationsSidebarOpen(false);
    useWorkspaceStore.getState().incrementDockLayoutKey();
    useWatchlistStore.getState().resetWatchlists();
    useBookByPriceStore.getState().reset();
    addNewChart();
    toastRef.current?.show({
      severity: "success",
      summary: "Novo Desktop",
      detail: "Novo desktop criado com sucesso",
      life: 3000,
    });
    // Menu will auto-hide when item is clicked
  };

  const handleImportWorkspace = async () => {
    try {
      // clearWorkspace is called via wrappedLoadAction before applying data
      const wrappedLoadAction = (...args) => {
        clearWorkspace();
        return loadWorkspaceAction(...args);
      };
      const success = await importWorkspace(wrappedLoadAction);
      if (success) {
        toastRef.current?.show({
          severity: "success",
          summary: "Desktop Importado",
          detail: "Desktop importado com sucesso",
          life: 3000,
        });
      } else {
        toastRef.current?.show({
          severity: "warn",
          summary: "Cancelado",
          detail: "Importação do desktop cancelada",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to import workspace:", error);

      // Provide more specific error messages
      let errorMessage =
        "Falha ao importar o desktop. Verifique o arquivo selecionado.";
      let errorSummary = "Erro ao Importar";

      if (error.message.includes("JSON")) {
        errorSummary = "Arquivo Inválido";
        errorMessage =
          "O arquivo selecionado não é um desktop válido. Verifique se é um arquivo .json de desktop.";
      } else if (error.message.includes("version")) {
        errorSummary = "Versão Incompatível";
        errorMessage = "O arquivo de desktop tem uma versão incompatível.";
      } else if (error.message.includes("read")) {
        errorSummary = "Erro de Leitura";
        errorMessage = "Não foi possível ler o arquivo selecionado.";
      }

      toastRef.current?.show({
        severity: "error",
        summary: errorSummary,
        detail: errorMessage,
        life: 5000,
      });
    }
    // Menu will auto-hide when item is clicked
  };

  const handleExportWorkspace = async () => {
    try {
      await exportWorkspace(chartsStore);
      toastRef.current?.show({
        severity: "success",
        summary: "Desktop Exportado",
        detail: "Desktop exportado com sucesso",
        life: 3000,
      });
    } catch (error) {
      console.error("Failed to export workspace:", error);

      // Show specific error messages based on the error type
      let errorMessage = "Falha ao exportar o desktop. Tente novamente.";
      let errorSummary = "Erro ao Exportar";

      if (
        error.message.includes("blocked") ||
        error.message.includes("security policy")
      ) {
        errorSummary = "Download Bloqueado";
        errorMessage =
          "O navegador bloqueou o download. Você pode copiar os dados para a área de transferência e salvá-los manualmente.";

        // Offer clipboard fallback
        try {
          await copyWorkspaceToClipboard(chartsStore);
          toastRef.current?.show({
            severity: "info",
            summary: "Dados Copiados",
            detail:
              "Os dados do desktop foram copiados para a área de transferência. Cole em um editor de texto e salve como arquivo .json.",
            life: 10000,
          });
        } catch (clipboardError) {
          console.error("Clipboard fallback also failed:", clipboardError);
          errorMessage += " A cópia para área de transferência também falhou.";
        }
      } else if (error.message.includes("Unable to download")) {
        errorSummary = "Erro de Download";
        errorMessage = "Não foi possível iniciar o download. Tente novamente.";
      }

      toastRef.current?.show({
        severity: "error",
        summary: errorSummary,
        detail: errorMessage,
        life: 8000,
      });
    }

    // Menu will auto-hide when item is clicked
  };

  // Remote Desktop Handlers
  const handleSaveDesktopRemote = async (name) => {
    try {
      // Check if name already exists
      const existingDesktop = await desktopNameExists(name);
      if (existingDesktop) {
        // Show confirmation dialog for override
        return new Promise((resolve, reject) => {
          toastRef.current?.show({
            severity: "warn",
            summary: "Desktop Já Existe",
            detail: `Um desktop com o nome "${name}" já existe. Deseja sobrescrever?`,
            sticky: true,
            content: (props) => (
              <div className="p-flex p-flex-column" style={{ flex: "1" }}>
                <div className="p-text-center">
                  <i
                    className="pi pi-exclamation-triangle"
                    style={{ fontSize: "3rem" }}
                  ></i>
                  <div
                    className="p-font-bold"
                    style={{ fontSize: "1.2rem", margin: "1rem 0" }}
                  >
                    {props.message.summary}
                  </div>
                  <p>{props.message.detail}</p>
                </div>
                <div className="p-grid p-fluid">
                  <div className="p-col-6">
                    <Button
                      type="button"
                      label="Cancelar"
                      className="p-button-secondary"
                      onClick={() => {
                        toastRef.current?.clear();
                        reject(new Error("Cancelled by user"));
                      }}
                      data-evo-id="navbar-save-dialog-cancel"
                    />
                  </div>
                  <div className="p-col-6">
                    <Button
                      type="button"
                      label="Sobrescrever"
                      className="p-button-warning"
                      onClick={async () => {
                        toastRef.current?.clear();
                        try {
                          // Override existing desktop by passing its ID
                          await saveDesktopRemote(chartsStore, name, {
                            id: existingDesktop.id,
                          });
                          setSaveDialogVisible(false);
                          toastRef.current?.show({
                            severity: "success",
                            summary: "Desktop Salvo",
                            detail: `Desktop "${name}" sobrescrito no servidor`,
                            life: 3000,
                          });
                          resolve();
                        } catch (err) {
                          reject(err);
                        }
                      }}
                      data-evo-id="navbar-save-dialog-overwrite"
                    />
                  </div>
                </div>
              </div>
            ),
          });
        });
      }

      // Save new desktop (id will be null)
      await saveDesktopRemote(chartsStore, name);
      setSaveDialogVisible(false);
      toastRef.current?.show({
        severity: "success",
        summary: "Desktop Salvo",
        detail: `Desktop "${name}" salvo no servidor`,
        life: 3000,
      });
    } catch (error) {
      if (error.message === "Cancelled by user") {
        return; // User cancelled, don't show error
      }
      console.error("Failed to save desktop remotely:", error);
      throw error;
    }
  };

  const handleLoadDesktopRemote = async (id) => {
    try {
      const wrappedLoadAction = (...args) => {
        clearWorkspace();
        return loadWorkspaceAction(...args);
      };
      await loadDesktopRemote(id, wrappedLoadAction);
      setLoadDialogVisible(false);
      toastRef.current?.show({
        severity: "success",
        summary: "Desktop Carregado",
        detail: "Desktop carregado do servidor com sucesso",
        life: 3000,
      });
    } catch (error) {
      console.error("Failed to load desktop remotely:", error);
      throw error;
    }
  };

  const handleDeleteDesktopRemote = async (id) => {
    try {
      await deleteDesktopRemote(id);
      toastRef.current?.show({
        severity: "success",
        summary: "Desktop Excluído",
        detail: "Desktop removido do servidor",
        life: 3000,
      });
    } catch (error) {
      console.error("Failed to delete desktop:", error);
      throw error;
    }
  };

  const handleRenameDesktopRemote = async (id, newName) => {
    try {
      await renameDesktopRemote(id, newName);
      toastRef.current?.show({
        severity: "success",
        summary: "Desktop Renomeado",
        detail: `Desktop renomeado para "${newName}"`,
        life: 3000,
      });
    } catch (error) {
      console.error("Failed to rename desktop:", error);
      throw error;
    }
  };

  const handleDuplicateDesktopRemote = async (id, newName) => {
    try {
      await duplicateDesktopRemote(id, newName);
      toastRef.current?.show({
        severity: "success",
        summary: "Desktop Duplicado",
        detail: `Desktop duplicado como "${newName}"`,
        life: 3000,
      });
    } catch (error) {
      console.error("Failed to duplicate desktop:", error);
      throw error;
    }
  };

  // Collapsible toolbar buttons — shared between inline and popup layouts
  const renderSidebarButtons = () => {
    const closePopup = () => {
      if (isNarrowViewport) setRightToolbarOpen(false);
    };
    return (
      <>
        {/* Fullscreen — only rendered here when narrow (otherwise inline) */}
        {isNarrowViewport && fullscreenSupported && (
          <>
            <button
              className="navbar-button codicon-btn"
              aria-label="Toggle fullscreen"
              data-tooltip={`${isFullscreen ? "Sair da tela cheia" : "Tela cheia"} (F11)`}
              onClick={() => {
                toggleFullscreen();
                closePopup();
              }}
              data-evo-id="navbar-btn-toggle-fullscreen"
            >
              <i
                className={`codicon ${isFullscreen ? "codicon-screen-normal" : "codicon-screen-full"} icon`}
              />
            </button>
            <Divider layout="vertical" />
          </>
        )}

        {/* ConfigPanel — only rendered here when narrow (otherwise inline) */}
        {isNarrowViewport && (
          <>
            <ConfigPanel />
            <Divider layout="vertical" />
          </>
        )}

        {/* ChartType & BarColor — only rendered here when narrow (otherwise in
            left navbar). Phone + context menu: hidden entirely (both are plain
            controls — the shared Cores modal is mounted at the navbar root,
            not in here, so unmounting is safe). */}
        {isNarrowViewport && !hideChartControls && (
          <>
            <div style={canChartType ? undefined : disabledStyle}>
              <ChartTypeDropdown />
            </div>
            <Divider layout="vertical" />
            <div style={canBarColor ? undefined : disabledStyle}>
              <BarColorDropdown />
            </div>
            <Divider layout="vertical" />
          </>
        )}

        {/* Organize actions — move into overflow on narrow viewports */}
        {isNarrowViewport && (
          <>
            <button
              className="navbar-button codicon-btn"
              aria-label="Organizar Gráficos em Abas"
              data-tooltip="Organizar Gráficos em Abas"
              onClick={() => {
                useWorkspaceStore.getState().setRequestOrganize("tabs");
                closePopup();
              }}
              data-evo-id="navbar-btn-organize-tabs"
            >
              <i className="codicon codicon-browser icon" />
            </button>
            <button
              className="navbar-button codicon-btn"
              aria-label="Organizar Gráficos em Grid"
              data-tooltip="Organizar Gráficos em Grid"
              onClick={() => {
                useWorkspaceStore.getState().setRequestOrganize("grid");
                closePopup();
              }}
              data-evo-id="navbar-btn-organize-grid"
            >
              <i className="pi pi-th-large icon" />
            </button>
            <Divider layout="vertical" />
          </>
        )}

        <button
          className="navbar-button codicon-btn"
          aria-label="Objects sidebar"
          data-tooltip={`${sidebarOpen ? "Recolher desenhos" : "Expandir desenhos"} (${modKey}+D)`}
          onClick={() => {
            toggleSidebar();
            closePopup();
          }}
          data-evo-id="navbar-btn-toggle-sidebar"
        >
          {sidebarOpen ? (
            <EditPenOn className="icon" width={20} height={20} />
          ) : (
            <EditPenOff className="icon" width={20} height={20} />
          )}
        </button>

        <Divider layout="vertical" />

        {/* RouterOperationsSidebar toggle button - hidden on mobile phones */}
        {!isMobilePhone() && (
          <button
            className="navbar-button codicon-btn"
            aria-label="Toggle RouterOperationsSidebar"
            data-tooltip={`${routerOperationsSidebarOpen ? "Recolher barra de negociação" : "Expandir barra de negociação"} (${modKey}+R)`}
            onClick={() => {
              toggleRouterOperationsSidebar();
              closePopup();
            }}
            data-evo-id="navbar-btn-toggle-router-operations-sidebar"
          >
            <i
              className={`codicon ${routerOperationsSidebarOpen ? "codicon-layout-sidebar-right" : "codicon-layout-sidebar-right-off"} icon`}
            />
          </button>
        )}

        {/* Chart selector ("Acesse os gráficos antigos"): web (browser) on
            Windows ONLY — never the native desktop app, never mac/mobile/tablet.
            Gate read at render time (isWebOnWindows). */}
        {isWebOnWindows() && (
          <>
            <Divider layout="vertical" />

            <button
              ref={chartSelectorButtonRef}
              className={`navbar-button codicon-btn ${showChartSelectorHighlight ? "chart-selector-highlight" : ""}`}
              aria-label="Seletor de gráficos"
              data-tooltip="Seletor de gráficos"
              data-tooltip-align="right"
              onClick={() => {
                handleOpenChartSelector();
                closePopup();
              }}
              data-evo-id="navbar-btn-chart-selector"
              style={{ position: "relative" }}
            >
              <i className="codicon codicon-graph icon" />
              {showChartSelectorHighlight && (
                <span className="chart-selector-badge">
                  Acesse os gráficos antigos
                </span>
              )}
            </button>
          </>
        )}
      </>
    );
  };

  // Desktop menu items
  const desktopMenuItems = [
    {
      label: "Novo",
      icon: "pi pi-plus",
      command: handleNewWorkspace,
    },
    {
      separator: true,
    },
    {
      label: "Salvar...",
      icon: "pi pi-save",
      command: () => setSaveDialogVisible(true),
    },
    {
      label: "Carregar...",
      icon: "pi pi-folder-open",
      command: () => setLoadDialogVisible(true),
    },
    {
      separator: true,
    },
    {
      label: "Exportar...",
      icon: "pi pi-download",
      command: handleExportWorkspace,
    },
    {
      label: "Importar...",
      icon: "pi pi-upload",
      command: handleImportWorkspace,
    },
  ];

  return (
    <nav
      className={`navbar${isFullscreen && isIPad ? " navbar-ipad-fullscreen" : ""}`}
    >
      <div className="navbar-content">
        {/* routerInfoPanelSideBar toggle button */}
        <div
          className="navbar-vscode-sidebars"
          style={{ marginRight: 0, marginLeft: 0 }}
        >
          <button
            className="navbar-button codicon-btn dock-btn"
            aria-label="Novo Gráfico"
            data-tooltip="Novo Gráfico"
            data-tooltip-align="left"
            onClick={createNewTab}
            data-evo-id="navbar-btn-new-chart"
          >
            <i className="chart-icon-candle icon" />
          </button>
          {isFeatureEnabled("infoPanel") && (
            <span className="dock-btn-wrap" data-tooltip="Info">
              <button
                className="navbar-button codicon-btn dock-btn"
                aria-label="Info"
                disabled={dockPanels.info}
                onClick={() => addDockPanel("info")}
                data-evo-id="navbar-btn-info"
              >
                <i className="codicon codicon-info icon" />
              </button>
            </span>
          )}
          <span className="dock-btn-wrap" data-tooltip="Posições">
            <button
              className="navbar-button codicon-btn dock-btn"
              aria-label="Posições"
              disabled={dockPanels.positions}
              onClick={() => addDockPanel("positions")}
              data-evo-id="navbar-btn-positions"
            >
              <i className="codicon codicon-briefcase icon" />
            </button>
          </span>
          <span className="dock-btn-wrap" data-tooltip="Ordens Abertas">
            <button
              className="navbar-button codicon-btn dock-btn"
              aria-label="Ordens Abertas"
              disabled={dockPanels.openOrders}
              onClick={() => addDockPanel("openOrders")}
              data-evo-id="navbar-btn-open-orders"
            >
              <i className="codicon codicon-list-selection icon" />
            </button>
          </span>
          <span className="dock-btn-wrap" data-tooltip="Log">
            <button
              className="navbar-button codicon-btn dock-btn"
              aria-label="Log"
              disabled={dockPanels.log}
              onClick={() => addDockPanel("log")}
              data-evo-id="navbar-btn-log"
            >
              <i className="codicon codicon-output icon" />
            </button>
          </span>
          <span className="dock-btn-wrap" data-tooltip="Watchlist">
            <button
              className="navbar-button codicon-btn dock-btn"
              aria-label="Watchlist"
              disabled={dockPanels.grid}
              onClick={() => addDockPanel("grid")}
              data-evo-id="navbar-btn-watchlist"
            >
              <i className="codicon codicon-eye icon" />
            </button>
          </span>
          <span className="dock-btn-wrap" data-tooltip="Livro de Ofertas">
            <button
              className="navbar-button codicon-btn dock-btn"
              aria-label="Livro de Ofertas"
              onClick={() => addDockPanel("bookByPrice")}
              data-evo-id="navbar-btn-book-by-price"
            >
              <i className="codicon codicon-list-ordered icon" />
            </button>
          </span>
          <Divider layout="vertical" />
          {/* Símbolo stays VISIBLE even in extended-menu mode (shows the
              current symbol, and it is the only symbol access for
              watchlist/book) — only the chart-config controls below hide. */}
          <div style={canSymbol ? undefined : disabledStyle}>
            <SymbolDropdown />
          </div>
          {!hideChartControls && <Divider layout="vertical" />}
          <div
            style={
              hideChartControls
                ? phoneHiddenStyle
                : canPeriodicity
                  ? undefined
                  : disabledStyle
            }
          >
            <PeriodicityDropdown />
          </div>
          {!hideChartControls && <Divider layout="vertical" />}
          <div
            style={
              hideChartControls
                ? phoneHiddenStyle
                : canIndicator
                  ? undefined
                  : disabledStyle
            }
          >
            <RegistryIndicatorsDropdown />
          </div>
          <div
            style={
              hideChartControls
                ? phoneHiddenStyle
                : canIndicator
                  ? undefined
                  : disabledStyle
            }
          >
            <Suspense fallback={<div></div>}>
              <ActiveIndicatorsModal ref={activeIndicatorsModalRef} />
            </Suspense>
          </div>
          {!isNarrowViewport && !hideChartControls && (
            <>
              <Divider layout="vertical" />
              <div style={canChartType ? undefined : disabledStyle}>
                <ChartTypeDropdown />
              </div>
              <Divider layout="vertical" />
              <div style={canBarColor ? undefined : disabledStyle}>
                <BarColorDropdown />
              </div>
            </>
          )}
        </div>
        <div className="login-roteador">
          {/* Fullscreen & ConfigPanel render inline only on wide viewports */}
          {!isNarrowViewport && (
            <>
              <button
                className="navbar-button codicon-btn"
                aria-label="Organizar Gráficos em Abas"
                data-tooltip="Organizar Gráficos em Abas"
                onClick={() =>
                  useWorkspaceStore.getState().setRequestOrganize("tabs")
                }
                data-evo-id="navbar-btn-organize-tabs"
              >
                <i className="codicon codicon-browser icon" />
              </button>
              <button
                className="navbar-button codicon-btn"
                aria-label="Organizar Gráficos em Grid"
                data-tooltip="Organizar Gráficos em Grid"
                onClick={() =>
                  useWorkspaceStore.getState().setRequestOrganize("grid")
                }
                data-evo-id="navbar-btn-organize-grid"
              >
                <i className="pi pi-th-large icon" />
              </button>
              {fullscreenSupported && (
                <button
                  className="navbar-button codicon-btn"
                  aria-label="Toggle fullscreen"
                  data-tooltip={`${isFullscreen ? "Sair da tela cheia" : "Tela cheia"} (F11)`}
                  onClick={toggleFullscreen}
                  data-evo-id="navbar-btn-toggle-fullscreen"
                >
                  <i
                    className={`codicon ${isFullscreen ? "codicon-screen-normal" : "codicon-screen-full"} icon`}
                  />
                </button>
              )}
              <ConfigPanel />
              <Divider layout="vertical" />
            </>
          )}
          {/* Sidebar/toolbar buttons — collapsible on narrow viewports */}
          {isNarrowViewport ? (
            <div style={{ position: "relative" }}>
              <button
                ref={toolbarTriggerRef}
                className="navbar-button codicon-btn"
                aria-label="Mais controles"
                data-tooltip="Mais controles"
                onClick={() => setRightToolbarOpen((prev) => !prev)}
                data-evo-id="navbar-btn-right-toolbar-trigger"
              >
                <i className="codicon codicon-ellipsis icon" />
              </button>
              {rightToolbarOpen && (
                <div ref={toolbarPopupRef} className="navbar-right-popup">
                  {renderSidebarButtons()}
                </div>
              )}
            </div>
          ) : (
            <div className="navbar-vscode-sidebars">
              {renderSidebarButtons()}
            </div>
          )}

          {/* Logout button removed (not implemented) */}
        </div>
      </div>

      <Toast ref={toastRef} position="top-center" />

      {/* Batch tester dialog - Feature flag: VITE_DEBUG_PANELS */}
      {DEBUG_PANELS_ENABLED && (
        <Suspense fallback={null}>
          <IndicatorBatchTester
            visible={batchTesterVisible}
            onHide={() => setBatchTesterVisible(false)}
          />
        </Suspense>
      )}

      {/* Chart selector modal */}
      <Suspense fallback={<div></div>}>
        <ChartSelectionModal
          visible={chartSelectorVisible}
          onDismiss={() => setChartSelectorVisible(false)}
          authToken={authToken || "demo-token"}
        />
      </Suspense>

      {/* Cores das barras — mounted HERE (not inside BarColorDropdown): the
          dropdown lives in viewport-conditional slots and is absent on phones,
          but the chart context menu's "Cores…" needs the modal on every
          viewport. One instance, shared by navbar button + context menu. */}
      {isFeatureEnabled("chartContextMenu") && <BarColorModal />}
    </nav>
  );
}

export default React.memo(Navbar);
